import React, { useState, useEffect } from "react";
import { fetchHiringManagers, GetDemandPlanning, fetchRolesForDepartment, saveHiringRequest, fetchActiveDepartments, fetchGradeRoles } from "../services/apiService";
import Select from 'react-select';
import { getUserFromToken, getToken, getDepartmentIdFromToken } from '../services/authService';
import toast from "react-hot-toast";
import { SendApprovalWorkflowEmailAsync } from "../services/sendMailSMTP";

const CreateHiringRequestModal = ({ isOpen, onClose, onSuccess }) => {
  const [departments, setDepartments] = useState([]);
  const [hiringManagers, setHiringManagers] = useState([]);
  const [rolesForDepartment, setRolesForDepartment] = useState([]);
  const [DemandPlanning, setDemandPlanning] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gradeRoles, setGradeRoles] = useState([]);
  const [selectedHiringManager, setSelectedHiringManager] = useState(null);
  const [step, setStep] = useState(1);
  const [isApprovalChecked, setIsApprovalChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showDocPreview, setShowDocPreview] = useState(false);

  const today = new Date();
  const datePlus120 = new Date(today.setDate(today.getDate() + 120));
  const datePlus120Str = datePlus120.toISOString().split("T")[0];

  const [newRequest, setNewRequest] = useState({
    department_id: "",
    job_description: "",
    job_description_file: "",
    hiring_manager: "",
    position: "",
    positionForcast: "",
    positionExeptional: "",
    reason_for_hire: "",
    planned_start_date: datePlus120Str,
    status: "",
    priority: "",
    position_type: "",
    hiring_Type: "",
    preferred_requirment: "",
    demand_id: "0",
    created_by: "",
    role_text: "",
    request_type: "",
    companyCode: "",
    years: new Date().getFullYear(),
    IsDepartmentRestricted: "",
    IsFinanceManagerApproveRequired: "",
    Documents: {
      FileName: "",
      FileBase64: "",
      Size: 0,
      MimeType: "",
      reference_id: "0",
      process_type: "",
      uploaded_by: "",
      uploaded_at: "",
      IsActive: true
    }
  });

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      loadData();
      const userName = getUserFromToken();
      setNewRequest(prev => ({ ...prev, created_by: userName }));
    }
  }, [isOpen]);

  const loadData = async () => {
    try {
      setLoading(true);
      const userRole = getToken();
      const userName = getUserFromToken();
      const [deptData, managers, dplan] = await Promise.all([
        fetchActiveDepartments(),
        fetchHiringManagers(),
        GetDemandPlanning(newRequest.years)
      ]);

      if (userRole.IsDepartmentRestricted === "True") {
        const departmentId = getDepartmentIdFromToken();
        setDepartments(deptData.filter(dept => String(dept.department_id) === String(departmentId)));
        setNewRequest(prev => ({ ...prev, department_id: departmentId }));
        setHiringManagers(managers.filter(manager => String(manager.username) === String(userName)));
        const filtered = managers.filter(
          (dept) => String(dept.username) === String(userName)
        );
        if (filtered.length > 0) {
          setSelectedHiringManager(filtered[0].user_id);
        }
        // Auto-load roles for the auto-selected department
        try {
          const currentYear = new Date().getFullYear();
          const roles = await fetchRolesForDepartment(currentYear, departmentId);
          setRolesForDepartment(roles);
        } catch (err) {
          console.error("Error loading roles for department:", err);
        }
      } else {
        setDepartments(deptData);
        setHiringManagers(managers);
      }

      setDemandPlanning(dplan);

      const grades = await fetchGradeRoles();
      if (grades && grades.length > 0) {
        setGradeRoles(grades);
      } else {
        setGradeRoles([
          { grade: 'A1', role_name: "CXO's" }, { grade: 'A2', role_name: 'Executive Director' },
          { grade: 'A3', role_name: 'Senior Director' }, { grade: 'A4', role_name: 'Director' },
          { grade: 'A5', role_name: 'Senior Manager' }, { grade: 'A6', role_name: 'Manager' },
          { grade: 'A7', role_name: 'Supervisor' }, { grade: 'A8', role_name: 'Senior Specialist' },
          { grade: 'A9', role_name: 'Specialist' }, { grade: 'A10', role_name: 'Senior Officer' },
          { grade: 'A11', role_name: 'Officer I' }, { grade: 'A12', role_name: 'Officer' },
          { grade: 'A13', role_name: 'Junior Officer' },
          { grade: 'T', role_name: 'Trainee' },
          { grade: 'C', role_name: 'Co-op' },
        ]);
      }
    } catch (err) {
      console.error("Failed to load data:", err);
      toast.error("Failed to load form data");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRequest(prev => ({ ...prev, [name]: value }));
  };

  const handleDepartmentChange = async (e) => {
    const selectedDeptId = e.target.value;
    setNewRequest(prev => ({ ...prev, department_id: selectedDeptId }));

    if (selectedDeptId) {
      try {
        const roles = await fetchRolesForDepartment(newRequest.years, selectedDeptId);
        setRolesForDepartment(roles);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    }
  };

  const handleYearChange = async (e) => {
    const selectedYear = e.target.value;
    setNewRequest(prev => ({ ...prev, years: selectedYear }));

    if (newRequest.department_id) {
      try {
        const roles = await fetchRolesForDepartment(selectedYear, newRequest.department_id);
        setRolesForDepartment(roles);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    }
  };

  const handleHiringManagerChange = (selectedOption) => {
    setSelectedHiringManager(selectedOption ? selectedOption.value : null);
  };

  const handleJobDescriptionFileUpload = (e) => {
    const file = e.target.files[0];
    const validTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];

    if (!file) {
      toast.error("No file selected.");
      return;
    }

    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Only PDF or image files are allowed.");
      e.target.value = null;
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size exceeds 5MB. Please upload a smaller file.");
      e.target.value = null;
      return;
    }

    const userName = getUserFromToken();
    const reader = new FileReader();

    reader.onloadend = () => {
      const base64String = reader.result.split(",")[1];
      setNewRequest(prev => ({
        ...prev,
        job_description_file: reader.result,
        Documents: {
          ...prev.Documents,
          FileName: file.name,
          FileBase64: base64String,
          Size: file.size,
          MimeType: file.type,
          reference_id: "0",
          process_type: "JobDescription",
          uploaded_by: userName,
          uploaded_at: new Date().toISOString(),
          IsActive: true,
        },
      }));
    };

    reader.readAsDataURL(file);
  };

  const nextStep = () => setStep(s => s + 1);
  const prevStep = () => setStep(s => s - 1);

  const validateForm = () => {
    const newErrors = {};

    if (!newRequest.department_id) newErrors.department_id = 'Department is required.';
    if (!newRequest.request_type) newErrors.request_type = "Request type is required.";

    if (newRequest.request_type === "Forecast") {
      if (!newRequest.demand_id) newErrors.demand_id = "Role is required.";
    } else if (newRequest.request_type === "Exceptional" || newRequest.request_type === "Outsourced") {
      if (!newRequest.role_text || newRequest.role_text.trim() === "") {
        newErrors.role_text = "Role is required.";
      }
    }

    if (!selectedHiringManager) newErrors.hiring_manager = 'Hiring Manager is required.';

    if (newRequest.request_type === "Forecast") {
      if (!newRequest.positionForcast) newErrors.position = 'Position is required.';
    } else if (newRequest.request_type === "Exceptional" || newRequest.request_type === "Outsourced") {
      if (!newRequest.positionExeptional) newErrors.position = 'Position is required.';
    }

    if (!newRequest.reason_for_hire) newErrors.reason_for_hire = 'Reason for Hire is required.';
    if (!newRequest.priority) newErrors.priority = 'Priority is required.';
    if (!newRequest.position_type) newErrors.position_type = 'Position Type is required.';
    if (!newRequest.hiring_Type) newErrors.hiring_Type = 'Hiring Type is required.';

    return newErrors;
  };

  const handleFormSubmit = async (status) => {
    try {
      const gradeToPosition = (grade) => {
        const matched = gradeRoles.find((g) => g.grade === grade);
        return matched ? `${grade}-${matched.role_name}` : grade;
      };
      const requestData = {
        ...newRequest,
        position: newRequest.request_type === "Forecast"
          ? gradeToPosition(newRequest.positionForcast)
          : gradeToPosition(newRequest.positionExeptional),
        planned_start_date: datePlus120Str,
        status: status,
        hiring_manager: selectedHiringManager,
        IsDepartmentRestricted: getToken().IsDepartmentRestricted === "True",
        created_by: getUserFromToken()
      };

      const validateFormFields = () => {
        const newErrors = {};

        if (!requestData.department_id) newErrors.department_id = 'Department is required.';
        if (!requestData.years) newErrors.years = 'Year is required.';
        if (!requestData.hiring_manager) newErrors.hiring_manager = 'Hiring manager is required.';
        if (!requestData.position) newErrors.position = 'Position is required.';
        if (!requestData.reason_for_hire) newErrors.reason_for_hire = 'Reason for hire is required.';
        if (!requestData.planned_start_date) newErrors.planned_start_date = 'Planned start date is required.';
        if (!requestData.priority) newErrors.priority = 'Priority is required.';
        if (!requestData.position_type) newErrors.position_type = 'Position type is required.';
        if (!requestData.hiring_Type) newErrors.hiring_Type = 'Hiring type is required.';

        if (newRequest.request_type === "Forecast") {
          requestData.IsFinanceManagerApproveRequired = false;
          if (!newRequest.demand_id) {
            newErrors.demand_id = "Role is required.";
          }
        } else if (newRequest.request_type === "Exceptional" || newRequest.request_type === "Outsourced") {
          requestData.IsFinanceManagerApproveRequired = true;
          if (!newRequest.role_text || newRequest.role_text.trim() === "") {
            newErrors.role_text = "Role is required.";
          }
        }

        return newErrors;
      };

      const CompanyCode = departments.find(
        (dept) => Number(dept.department_id) === Number(requestData.department_id)
      )?.company || "";

      const validationErrors = validateFormFields();

      if (Object.keys(validationErrors).length > 0) {
        Object.entries(validationErrors).forEach(([field, message]) => {
          toast.error(message);
        });
        return;
      }

      setErrors({});
      setIsSubmitting(true);
      requestData.companyCode = CompanyCode;

      const savedRequest = await saveHiringRequest(requestData);

      if (Number(savedRequest.status) === 400) {
        toast.error("Hiring request cannot be created as Requested Count exceeds forecasted hires");
        setIsSubmitting(false);
        return;
      } else if (savedRequest.status === 500) {
        toast.error("Failed to save the hiring request.");
        setIsSubmitting(false);
        return;
      }

      toast.success("Hiring request saved successfully!");

      const userRole = getToken();
      if (status === "Submitted") {
        try {
          const roles = requestData.request_type === "Forecast"
            ? DemandPlanning.find(manager => Number(manager.demand_id) === Number(requestData.demand_id))?.role
            : requestData.role_text;

          const departmentName = departments.find(
            (dept) => Number(dept.department_id) === Number(requestData.department_id)
          )?.department_name || "";

          const isExcOrOut = requestData.request_type === "Exceptional" || requestData.request_type === "Outsourced";
      debugger
          await SendApprovalWorkflowEmailAsync({
            recipientRole: isExcOrOut ? "Finance Manager" : "HR Director",
            companyCode: CompanyCode || getToken()?.companyCode || '',
            hiringManagerUserId: Number(selectedHiringManager) || null,
            requestId: savedRequest.data?.hiringRequestId || savedRequest.data?.request_id,
            referenceNumber: '', action: "Submitted", actorName: getUserFromToken(), actorRole: "Business Owner",
            nextStep: isExcOrOut ? "Pending Finance Approval" : "Pending HR Director Approval",
            notes: '', department: departmentName, position: requestData.position || '', requestType: requestData.request_type || '',
          });
          toast.success("Email notification sent!");

        } catch (emailError) {
          console.error("Failed to send email to HR:", emailError);
          toast.warn("Request saved but email notification failed.");
        }
      }

      resetForm();
      onSuccess();
      onClose();

    } catch (error) {
      console.error('Error saving hiring request:', error);
      toast.error("An error occurred while saving the hiring request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setNewRequest({
      department_id: "",
      job_description: "",
      job_description_file: "",
      hiring_manager: "",
      position: "",
      positionForcast: "",
      positionExeptional: "",
      reason_for_hire: "",
      planned_start_date: datePlus120Str,
      status: "",
      priority: "",
      position_type: "",
      hiring_Type: "",
      preferred_requirment: "",
      demand_id: "0",
      created_by: getUserFromToken(),
      role_text: "",
      request_type: "",
      companyCode: "",
      years: new Date().getFullYear(),
      IsDepartmentRestricted: "",
      IsFinanceManagerApproveRequired: "",
      Documents: {
        FileName: "",
        FileBase64: "",
        Size: 0,
        MimeType: "",
        reference_id: "0",
        process_type: "",
        uploaded_by: "",
        uploaded_at: "",
        IsActive: true
      }
    });
    setStep(1);
    setIsApprovalChecked(false);
    setSelectedHiringManager(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.2)' }}>
      <div
        className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl xl:max-w-5xl"
        style={{
          borderColor: 'rgba(18, 76, 114, 0.1)',
          borderWidth: '1px',
        }}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b rounded-t-2xl" style={{
          borderColor: 'rgba(18, 76, 114, 0.1)',
          background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)'
        }}>
          <h2 className="text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>
            Create New Hiring Request
          </h2>
          <button
            onClick={onClose}
            className="transition-all rounded-full p-1.5"
            style={{ color: 'rgba(18, 76, 114, 0.6)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgb(18, 76, 114)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'rgba(18, 76, 114, 0.6)';
            }}
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between w-full px-[4%] py-4 rounded-xl"
            style={{ backgroundColor: 'rgba(18, 76, 114, 0.03)', border: '1px solid rgba(18, 76, 114, 0.1)' }}>
            {[1, 2, 3].map((stepNum) => (
              <React.Fragment key={stepNum}>
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-full font-bold text-lg transition-all duration-300 ${step >= stepNum ? "bg-rgb(18, 76, 114) text-white shadow-md" : "bg-gray-100 text-gray-400"}`}
                    style={step >= stepNum ? { backgroundColor: 'rgb(18, 76, 114)' } : {}}>
                    {stepNum}
                  </div>
                  <span className={`mt-2 text-sm font-medium transition-colors ${step >= stepNum ? "text-rgb(18, 76, 114)" : "text-gray-400"}`}
                    style={step >= stepNum ? { color: 'rgb(18, 76, 114)' } : {}}>
                    {stepNum === 1 ? "Details" : stepNum === 2 ? "Job Details" : "Review"}
                  </span>
                </div>
                {stepNum < 3 && (
                  <div className={`flex-1 h-1 mx-2 rounded transition-all duration-300 ${step > stepNum ? "bg-rgb(18, 76, 114)" : "bg-gray-200"}`}
                    style={step > stepNum ? { backgroundColor: 'rgb(18, 76, 114)' } : {}}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-6 max-h-[90vh] overflow-y-auto">
          <form className="space-y-6">
            {step === 1 && (
              <Step1
                newRequest={newRequest}
                departments={departments}
                rolesForDepartment={rolesForDepartment}
                DemandPlanning={DemandPlanning}
                hiringManagers={hiringManagers}
                selectedHiringManager={selectedHiringManager}
                handleInputChange={handleInputChange}
                handleDepartmentChange={handleDepartmentChange}
                handleYearChange={handleYearChange}
                handleHiringManagerChange={handleHiringManagerChange}
                datePlus120Str={datePlus120Str}
                userRole={getToken()}
                errors={errors}
                nextStep={nextStep}
                gradeRoles={gradeRoles}
              />
            )}

            {step === 2 && (
              <Step2
                newRequest={newRequest}
                setNewRequest={setNewRequest}
                handleInputChange={handleInputChange}
                handleJobDescriptionFileUpload={handleJobDescriptionFileUpload}
                DemandPlanning={DemandPlanning}
                rolesForDepartment={rolesForDepartment}
                setShowDocPreview={setShowDocPreview}
                prevStep={prevStep}
                nextStep={nextStep}
              />
            )}

            {step === 3 && (
              <Step3
                newRequest={newRequest}
                departments={departments}
                rolesForDepartment={rolesForDepartment}
                DemandPlanning={DemandPlanning}
                hiringManagers={hiringManagers}
                selectedHiringManager={selectedHiringManager}
                datePlus120Str={datePlus120Str}
                isApprovalChecked={isApprovalChecked}
                setIsApprovalChecked={setIsApprovalChecked}
                setShowDocPreview={setShowDocPreview}
                prevStep={prevStep}
                handleFormSubmit={handleFormSubmit}
                isSubmitting={isSubmitting}
                handleClose={handleClose}
              />
            )}
          </form>
        </div>

        {showDocPreview && (
          <DocumentPreview
            newRequest={newRequest}
            DemandPlanning={DemandPlanning}
            setShowDocPreview={setShowDocPreview}
          />
        )}
      </div>
    </div>
  );
};

// ─── Step 1 Component - Details ───────────────────────────────────────────────
const Step1 = ({
  newRequest, departments, rolesForDepartment, DemandPlanning,
  hiringManagers, selectedHiringManager, handleInputChange,
  handleDepartmentChange, handleYearChange, handleHiringManagerChange,
  datePlus120Str, userRole, errors, nextStep, gradeRoles
}) => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Hiring Request Type */}
      <div className="space-y-2">
        <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
          Hiring Request Type <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <select
            name="request_type"
            value={newRequest.request_type}
            onChange={handleInputChange}
            className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 appearance-none"
            style={{
              borderColor: !newRequest.request_type ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)',
              backgroundColor: 'white',
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 0.5rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.5em 1.5em',
              paddingRight: '2.5rem'
            }}
            onFocus={(e) => {
              e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
              e.target.style.borderColor = 'rgb(18, 76, 114)';
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = 'none';
              e.target.style.borderColor = !newRequest.request_type ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)';
            }}
          >
            <option value="">Select Type</option>
            <option value="Forecast">Forecast</option>
            <option value="Exceptional">Exceptional</option>
            <option value="Outsourced">Outsourced</option>
          </select>
        </div>
        {errors.request_type && <div className="text-red-500 text-xs mt-1">{errors.request_type}</div>}
      </div>

      {/* Department */}
      <div className="space-y-2">
        <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
          Department <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <select
            name="department_id"
            value={newRequest.department_id}
            onChange={handleDepartmentChange}
            className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 appearance-none"
            style={{
              borderColor: !newRequest.department_id ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)',
              backgroundColor: 'white',
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 0.5rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.5em 1.5em',
              paddingRight: '2.5rem'
            }}
            onFocus={(e) => {
              e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
              e.target.style.borderColor = 'rgb(18, 76, 114)';
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = 'none';
              e.target.style.borderColor = !newRequest.department_id ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)';
            }}
          >
            <option value="">Select Department</option>
            {departments.map((d) => (
              <option key={d.department_id} value={d.department_id}>
                {d.department_name}
              </option>
            ))}
          </select>
        </div>
        {errors.department_id && <div className="text-red-500 text-xs mt-1">{errors.department_id}</div>}
      </div>

      {/* Year */}
      <div className="space-y-2">
        <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
          Year <span className="text-red-500">*</span>
        </label>
        {(newRequest.request_type === "Exceptional" || newRequest.request_type === "Outsourced") ? (
          <div className="relative">
            <input
              type="text"
              value={new Date().getFullYear()}
              readOnly
              className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700"
              style={{
                borderColor: 'rgba(18, 76, 114, 0.3)',
                backgroundColor: 'rgba(18, 76, 114, 0.05)'
              }}
            />
          </div>
        ) : (
          <div className="relative">
            <select
              name="years"
              value={newRequest.years}
              onChange={handleYearChange}
              className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 appearance-none"
              style={{
                borderColor: !newRequest.years ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)',
                backgroundColor: 'white',
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.5em 1.5em',
                paddingRight: '2.5rem'
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                e.target.style.borderColor = 'rgb(18, 76, 114)';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
                e.target.style.borderColor = !newRequest.years ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)';
              }}
            >
              {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>

    {/* Role & Position */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Role */}
      <div className="space-y-2">
        <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
          Role <span className="text-red-500">*</span>
        </label>
        {(newRequest.request_type === "Exceptional" || newRequest.request_type === "Outsourced") ? (
          <div className="relative">
            <input
              type="text"
              name="role_text"
              value={newRequest.role_text}
              onChange={handleInputChange}
              placeholder="Enter Role"
              className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700"
              style={{
                borderColor: !newRequest.role_text ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)',
                backgroundColor: 'white'
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                e.target.style.borderColor = 'rgb(18, 76, 114)';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
                e.target.style.borderColor = !newRequest.role_text ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)';
              }}
            />
          </div>
        ) : (
          <div className="relative">
            <select
              name="demand_id"
              value={newRequest.demand_id}
              onChange={handleInputChange}
              className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 appearance-none"
              style={{
                borderColor: !newRequest.demand_id ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)',
                backgroundColor: 'white',
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.5em 1.5em',
                paddingRight: '2.5rem'
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                e.target.style.borderColor = 'rgb(18, 76, 114)';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
                e.target.style.borderColor = !newRequest.demand_id ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)';
              }}
            >
              <option value="">Select Role</option>
              {rolesForDepartment.map((role) => (
                <option key={role.demand_id} value={role.demand_id}>
                  {role.role}
                </option>
              ))}
            </select>
          </div>
        )}
        {errors.demand_id && newRequest.request_type === "Forecast" && (
          <div className="text-red-500 text-xs mt-1">{errors.demand_id}</div>
        )}
        {errors.role_text && (newRequest.request_type === "Exceptional" || newRequest.request_type === "Outsourced") && (
          <div className="text-red-500 text-xs mt-1">{errors.role_text}</div>
        )}
      </div>

      {/* Position */}
      <div className="space-y-2">
        <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
          Position <span className="text-red-500">*</span>
        </label>
        {newRequest.request_type === "Forecast" ? (
          <div className="relative">
            <select
              name="positionForcast"
              value={newRequest.positionForcast}
              onChange={handleInputChange}
              className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 appearance-none"
              style={{
                borderColor: !newRequest.positionForcast ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)',
                backgroundColor: 'white',
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.5em 1.5em',
                paddingRight: '2.5rem'
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                e.target.style.borderColor = 'rgb(18, 76, 114)';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
                e.target.style.borderColor = !newRequest.positionForcast ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)';
              }}
            >
              <option value="">Select Position</option>
              {rolesForDepartment
                .filter(role => role.demand_id === parseInt(newRequest.demand_id, 10))
                .map(role => (
                  <option key={`${role.demand_id}-${role.grade}`} value={role.grade}>
                    {role.grade}{role.role ? ` - ${role.role}` : ''}
                  </option>
                ))}
            </select>
          </div>
        ) : (
          <div className="relative">
            <select
              name="positionExeptional"
              value={newRequest.positionExeptional}
              onChange={handleInputChange}
              className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 appearance-none"
              style={{
                borderColor: !newRequest.positionExeptional ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)',
                backgroundColor: 'white',
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.5em 1.5em',
                paddingRight: '2.5rem'
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                e.target.style.borderColor = 'rgb(18, 76, 114)';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
                e.target.style.borderColor = !newRequest.positionExeptional ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)';
              }}
            >
              <option value="">Select Position</option>
              {[...gradeRoles].sort((a, b) => {
                  const aNum = parseInt(a.grade.slice(1));
                  const bNum = parseInt(b.grade.slice(1));
                  if (isNaN(aNum) && isNaN(bNum)) return a.grade.localeCompare(b.grade);
                  if (isNaN(aNum)) return 1;
                  if (isNaN(bNum)) return 1;
                  return aNum - bNum;
                }).map((g) => (
                <option key={g.grade} value={g.grade}>
                  {g.grade} - {g.role_name}
                </option>
              ))}
            </select>
          </div>
        )}
        {errors.position && <div className="text-red-500 text-xs mt-1">{errors.position}</div>}
      </div>

      {/* Hiring Manager */}
      <div className="space-y-2">
        <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
          Hiring Manager <span className="text-red-500">*</span>
        </label>
        {userRole.IsDepartmentRestricted === "True" ? (
          <div className="p-2.5 rounded-lg"
            style={{
              backgroundColor: 'rgba(18, 76, 114, 0.05)',
              border: '1px solid rgba(18, 76, 114, 0.1)'
            }}>
            <span className="text-gray-700">
              {hiringManagers.length > 0
                ? `${hiringManagers[0].first_name} ${hiringManagers[0].last_name}`
                : "No hiring manager found"}
            </span>
          </div>
        ) : (
          <Select
            className="w-full rounded-lg text-gray-700"
            options={hiringManagers.map(m => ({ value: m.user_id, label: `${m.first_name} ${m.last_name}` }))}
            value={selectedHiringManager ? {
              value: selectedHiringManager,
              label: `${hiringManagers.find(m => m.user_id === selectedHiringManager)?.first_name} ${hiringManagers.find(m => m.user_id === selectedHiringManager)?.last_name}`
            } : null}
            onChange={handleHiringManagerChange}
            placeholder="Select Hiring Manager"
            styles={{
              control: (base, state) => ({
                ...base,
                borderColor: state.isFocused ? 'rgb(18, 76, 114)' : !selectedHiringManager ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)',
                boxShadow: state.isFocused ? '0 0 0 2px rgba(18, 76, 114, 0.2)' : 'none',
                backgroundColor: 'white',
                minHeight: '44px',
                '&:hover': {
                  borderColor: 'rgb(18, 76, 114)'
                }
              })
            }}
          />
        )}
        {errors.hiring_manager && <div className="text-red-500 text-xs mt-1">{errors.hiring_manager}</div>}
      </div>
    </div>

    {/* Planned Start & Priority */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
          Planned Start Date
        </label>
        <div className="relative">
          <input
            type="date"
            name="planned_start_date"
            value={newRequest.planned_start_date || datePlus120Str}
            onChange={handleInputChange}
            disabled
            className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700"
            style={{
              borderColor: 'rgba(18, 76, 114, 0.3)',
              backgroundColor: 'rgba(18, 76, 114, 0.05)'
            }}
          />
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
          Priority <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <select
            name="priority"
            value={newRequest.priority}
            onChange={handleInputChange}
            className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 appearance-none"
            style={{
              borderColor: !newRequest.priority ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)',
              backgroundColor: 'white',
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 0.5rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.5em 1.5em',
              paddingRight: '2.5rem'
            }}
            onFocus={(e) => {
              e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
              e.target.style.borderColor = 'rgb(18, 76, 114)';
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = 'none';
              e.target.style.borderColor = !newRequest.priority ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)';
            }}
          >
            <option value="">Select Priority</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>
        {errors.priority && <div className="text-red-500 text-xs mt-1">{errors.priority}</div>}
      </div>
    </div>

    <div className="flex justify-end mt-6 pt-4 border-t" style={{ borderColor: 'rgba(18, 76, 114, 0.1)' }}>
      <button
        type="button"
        onClick={nextStep}
        className="px-5 py-2.5 text-white font-medium rounded-lg transition-all"
        style={{
          backgroundColor: 'rgb(18, 76, 114)',
          cursor: 'pointer',
          boxShadow: '0 10px 15px -3px rgba(18, 76, 114, 0.2)'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
      >
        Next Step
      </button>
    </div>
  </>
);

// ─── Step 2 Component - Job Declaration ───────────────────────────────────────
const Step2 = ({
  newRequest, setNewRequest, handleInputChange, handleJobDescriptionFileUpload,
  DemandPlanning, rolesForDepartment, setShowDocPreview,
  prevStep, nextStep
}) => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* Position Type */}
      <div className="space-y-2">
        <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
          Position Type <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <select
            name="position_type"
            value={newRequest.position_type}
            onChange={handleInputChange}
            className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 appearance-none"
            style={{
              borderColor: !newRequest.position_type ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)',
              backgroundColor: 'white',
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 0.5rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.5em 1.5em',
              paddingRight: '2.5rem'
            }}
            onFocus={(e) => {
              e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
              e.target.style.borderColor = 'rgb(18, 76, 114)';
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = 'none';
              e.target.style.borderColor = !newRequest.position_type ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)';
            }}
          >
            <option value="">Select Position Type</option>
            <option value="Full-time">Full-time</option>
            <option value="Part-time">Part-time</option>
           
          </select>
        </div>
      </div>

      {/* Hiring Type */}
      <div className="space-y-2">
        <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
          Hiring Type <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <select
            name="hiring_Type"
            value={newRequest.hiring_Type}
            onChange={handleInputChange}
            className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 appearance-none"
            style={{
              borderColor: !newRequest.hiring_Type ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)',
              backgroundColor: 'white',
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 0.5rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.5em 1.5em',
              paddingRight: '2.5rem'
            }}
            onFocus={(e) => {
              e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
              e.target.style.borderColor = 'rgb(18, 76, 114)';
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = 'none';
              e.target.style.borderColor = !newRequest.hiring_Type ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)';
            }}
          >
            <option value="">Select Hiring Type</option>
            <option value="New">New</option>
            {(newRequest.request_type === "Exceptional" || newRequest.request_type === "Outsourced") && (
              <option value="Replacement">Replacement</option>
            )}
          </select>
        </div>
      </div>

      {/* Job Description Upload — Exceptional & Contract */}
      {(newRequest.request_type === "Exceptional" || newRequest.request_type === "Outsourced") && (
        <div className="sm:col-span-2 space-y-2">
          <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
            Upload Job Description
          </label>
          <div className="flex gap-3 items-center">
            {/* Upload Area */}
            <div className="relative flex-1">
              <input
                type="file"
                name="job_description_file"
                accept=".pdf"
                onChange={handleJobDescriptionFileUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <div className="px-4 py-3 border rounded-lg transition-all"
                style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }}>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }}>
                    <svg className="w-5 h-5" style={{ color: 'rgb(18, 76, 114)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium truncate" style={{ color: 'rgb(18, 76, 114)' }}>
                      {newRequest.Documents?.FileName || 'Click to upload job description'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {newRequest.Documents?.FileName
                        ? `(${(newRequest.Documents?.Size / 1024).toFixed(2)} KB)`
                        : 'Supports: Only PDF (Max 5MB)'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* View Button */}
            {newRequest.Documents?.FileBase64 && (
              <button
                type="button"
                onClick={() => setShowDocPreview(true)}
                className="px-4 py-2.5 border rounded-lg transition-all font-medium flex items-center gap-2"
                style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                View
              </button>
            )}

            {/* Remove Button */}
            {newRequest.Documents?.FileBase64 && (
              <button
                type="button"
                onClick={() => {
                  setNewRequest((prev) => ({
                    ...prev,
                    job_description_file: null,
                    Documents: {
                      ...prev.Documents,
                      FileName: "",
                      FileBase64: "",
                      Size: 0,
                      MimeType: "",
                      reference_id: "0",
                      process_type: "JobDescription",
                      uploaded_by: "",
                      uploaded_at: "",
                      IsActive: false,
                    }
                  }));
                }}
                className="px-4 py-2.5 border rounded-lg transition-all font-medium flex items-center gap-2"
                style={{ color: '#dc2626', borderColor: 'rgba(220, 38, 38, 0.3)', backgroundColor: 'white' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Remove
              </button>
            )}
          </div>
        </div>
      )}

      {/* Attached Job Description — Forecast only */}
      {newRequest.request_type === "Forecast" && newRequest.demand_id && (() => {
        const matchedDemand = DemandPlanning.find(d => d.demand_id === parseInt(newRequest.demand_id));
        const fileName = matchedDemand?.documents?.fileName;
        const fileSize = matchedDemand?.documents?.size;
        const hasFile = matchedDemand?.documents?.fileBase64;

        return (
          <div className="space-y-2">
            <label className="block text-xs md:text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
              Attached Job Description
            </label>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <div className="px-4 py-3 border rounded-lg transition-all"
                  style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }}>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }}>
                      <svg className="w-5 h-5" style={{ color: 'rgb(18, 76, 114)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: 'rgb(18, 76, 114)' }}>
                        {fileName || 'No document attached'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {fileName
                          ? `(${fileSize ? (fileSize / 1024).toFixed(2) : '0.00'} KB)`
                          : 'Document from forecast demand plan'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {hasFile && (
                <button
                  type="button"
                  onClick={() => setShowDocPreview(true)}
                  className="px-4 py-2.5 border rounded-lg transition-all font-medium flex items-center gap-2"
                  style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.1)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Preview
                </button>
              )}
            </div>
          </div>
        );
      })()}
    </div>

    {/* Reason for Hire */}
    <div className="space-y-2 mt-4">
      <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
        Reason for Hire <span className="text-red-500">*</span>
      </label>
      <textarea
        name="reason_for_hire"
        value={newRequest.reason_for_hire}
        onChange={handleInputChange}
        rows={1}
        className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 resize-vertical"
        style={{
          borderColor: !newRequest.reason_for_hire ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)',
          backgroundColor: 'white'
        }}
        placeholder="Enter the reason for this hire..."
        onFocus={(e) => {
          e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
          e.target.style.borderColor = 'rgb(18, 76, 114)';
        }}
        onBlur={(e) => {
          e.target.style.boxShadow = 'none';
          e.target.style.borderColor = !newRequest.reason_for_hire ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.5)';
        }}
      />
    </div>

    {/* Preferred Requirement — Exceptional & Contract */}
    {(newRequest.request_type === "Exceptional" || newRequest.request_type === "Outsourced") && (
      <div className="space-y-2 mt-4">
        <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
          Preferred Requirement
        </label>
        <textarea
          name="preferred_requirment"
          value={newRequest.preferred_requirment}
          onChange={handleInputChange}
          rows={1}
          className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 resize-vertical"
          style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }}
          placeholder="Enter preferred requirements..."
          onFocus={(e) => {
            e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
            e.target.style.borderColor = 'rgb(18, 76, 114)';
          }}
          onBlur={(e) => {
            e.target.style.boxShadow = 'none';
            e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
          }}
        />
      </div>
    )}

    <div className="flex justify-between mt-6 pt-4 border-t" style={{ borderColor: 'rgba(18, 76, 114, 0.1)' }}>
      <button
        type="button"
        onClick={prevStep}
        className="px-5 py-2.5 bg-white border rounded-lg transition-all font-medium"
        style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.3)' }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.1)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
      >
        Back
      </button>
      <button
        type="button"
        onClick={nextStep}
        className="px-5 py-2.5 text-white font-medium rounded-lg transition-all"
        style={{ backgroundColor: 'rgb(18, 76, 114)', cursor: 'pointer', boxShadow: '0 10px 15px -3px rgba(18, 76, 114, 0.2)' }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
      >
        Next Step
      </button>
    </div>
  </>
);

// ─── Step 3 Component - Finalize ──────────────────────────────────────────────
const Step3 = ({
  newRequest, departments, rolesForDepartment, DemandPlanning,
  hiringManagers, selectedHiringManager, datePlus120Str,
  isApprovalChecked, setIsApprovalChecked, setShowDocPreview,
  prevStep, handleFormSubmit, isSubmitting, handleClose
}) => (
  <>
    <div className="flex flex-col h-full max-h-[80vh]">

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto pr-2">
        <h3 className="text-lg font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>
          Review Details
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">

          {/* Request Type */}
          <div className="space-y-1">
            <span className="text-xs font-medium" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>Request Type</span>
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.05)', border: '1px solid rgba(18, 76, 114, 0.1)' }}>
              <span className="text-gray-700">{newRequest.request_type}</span>
            </div>
          </div>

          {/* Department */}
          <div className="space-y-1">
            <span className="text-xs font-medium" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>Department</span>
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.05)', border: '1px solid rgba(18, 76, 114, 0.1)' }}>
              <span className="text-gray-700">
                {departments.find(d => Number(d.department_id) === Number(newRequest.department_id))?.department_name || '-'}
              </span>
            </div>
          </div>

          {/* Year */}
          <div className="space-y-1">
            <span className="text-xs font-medium" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>Year</span>
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.05)', border: '1px solid rgba(18, 76, 114, 0.1)' }}>
              <span className="text-gray-700">{newRequest.years}</span>
            </div>
          </div>

          {/* Role */}
          <div className="space-y-1">
            <span className="text-xs font-medium" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>Role</span>
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.05)', border: '1px solid rgba(18, 76, 114, 0.1)' }}>
              <span className="text-gray-700">
                {(newRequest.request_type === "Exceptional" || newRequest.request_type === "Outsourced")
                  ? newRequest.role_text
                  : rolesForDepartment.find(r => Number(r.demand_id) === Number(newRequest.demand_id))?.role || '-'}
              </span>
            </div>
          </div>

          {/* Position */}
          <div className="space-y-1">
            <span className="text-xs font-medium" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>Position</span>
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.05)', border: '1px solid rgba(18, 76, 114, 0.1)' }}>
              <span className="text-gray-700">
                {(newRequest.request_type === "Exceptional" || newRequest.request_type === "Outsourced")
                  ? newRequest.positionExeptional
                  : newRequest.positionForcast}
              </span>
            </div>
          </div>

          {/* Hiring Manager */}
          <div className="space-y-1">
            <span className="text-xs font-medium" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>Hiring Manager</span>
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.05)', border: '1px solid rgba(18, 76, 114, 0.1)' }}>
              <span className="text-gray-700">
                {hiringManagers.find(m => m.user_id === selectedHiringManager)
                  ? `${hiringManagers.find(m => m.user_id === selectedHiringManager)?.first_name} ${hiringManagers.find(m => m.user_id === selectedHiringManager)?.last_name}`
                  : 'N/A'}
              </span>
            </div>
          </div>

          {/* Attached Document */}
          {newRequest.Documents?.FileName && (
            <div className="sm:col-span-2 space-y-2">
              <label className="block text-xs font-medium" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>
                Attached Document
              </label>
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <div className="px-4 py-3 border rounded-lg transition-all"
                    style={{ borderColor: 'rgba(18, 76, 114, 0.2)', backgroundColor: 'rgba(18, 76, 114, 0.03)' }}>
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }}>
                        <svg className="w-5 h-5" style={{ color: 'rgb(18, 76, 114)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: 'rgb(18, 76, 114)' }}>
                          {newRequest.Documents.FileName}
                        </p>
                        <p className="text-xs text-gray-500">
                          Size: {(newRequest.Documents.Size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDocPreview(true)}
                  className="px-4 py-2.5 border rounded-lg transition-all font-medium flex items-center gap-2"
                  style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.1)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Preview
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Approval Checkbox */}
        <div className="flex items-center mt-6 p-3 rounded-lg"
          style={{ backgroundColor: 'rgba(18, 76, 114, 0.03)', border: '1px solid rgba(18, 76, 114, 0.1)' }}>
          <input
            type="checkbox"
            id="approvalCheck"
            checked={isApprovalChecked}
            onChange={(e) => setIsApprovalChecked(e.target.checked)}
            className="mr-3 w-5 h-5"
            style={{ accentColor: 'rgb(18, 76, 114)' }}
          />
          <label htmlFor="approvalCheck" className="text-sm text-gray-700">
            I confirm that all information provided is accurate and ready for approval.
          </label>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="sticky bottom-0 bg-white pt-4 mt-4 border-t z-10"
        style={{ borderColor: 'rgba(18, 76, 114, 0.1)' }}>
        <div className="flex justify-between">
          <button
            type="button"
            onClick={prevStep}
            className="px-5 py-2.5 bg-white border rounded-lg font-medium"
            style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.3)' }}
          >
            Back
          </button>

          <div className="flex gap-3">
            {!isApprovalChecked ? (
              <button
                type="button"
                onClick={() => handleFormSubmit("Draft")}
                disabled={isSubmitting}
                className="px-5 py-2.5 text-white font-medium rounded-lg"
                style={{ backgroundColor: isSubmitting ? 'rgba(18, 76, 114, 0.5)' : 'rgba(18, 76, 114, 0.8)' }}
              >
                {isSubmitting ? "Saving..." : "Save as Draft"}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => handleFormSubmit("Submitted")}
                disabled={isSubmitting}
                className="px-5 py-2.5 text-white font-medium rounded-lg"
                style={{ backgroundColor: isSubmitting ? 'rgba(18, 76, 114, 0.5)' : 'rgb(18, 76, 114)' }}
              >
                {isSubmitting ? "Submitting..." : "Submit for Approval"}
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="px-5 py-2.5 bg-white border rounded-lg font-medium"
              style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.3)' }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </>
);

// ─── Document Preview Component ───────────────────────────────────────────────
const DocumentPreview = ({ newRequest, DemandPlanning, setShowDocPreview }) => {
  const getDocumentData = () => {
    if (newRequest.request_type === "Exceptional" || newRequest.request_type === "Outsourced") {
      return {
        fileName: newRequest.Documents?.FileName || "No File",
        mimeType: newRequest.Documents?.MimeType,
        fileBase64: newRequest.Documents?.FileBase64,
        title: newRequest.request_type === "Outsourced" ? "Outsourced Request" : "Exceptional Request"
      };
    } else {
      const matchedDemand = DemandPlanning.find(
        (d) => d.demand_id === parseInt(newRequest.demand_id)
      );
      return {
        fileName: matchedDemand?.documents?.fileName || "No File",
        mimeType: matchedDemand?.documents?.mimeType,
        fileBase64: matchedDemand?.documents?.fileBase64,
        title: "Forecast Planning"
      };
    }
  };

  const docData = getDocumentData();

  return (
    <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center z-[10000] p-4"
      style={{ backgroundColor: 'rgba(18, 76, 114, 0.2)' }}>
      <div
        className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-4xl h-[80vh]"
        style={{ borderColor: 'rgba(18, 76, 114, 0.1)', borderWidth: '1px' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b rounded-t-2xl" style={{
          borderColor: 'rgba(18, 76, 114, 0.1)',
          background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)'
        }}>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold truncate" style={{ color: 'rgb(18, 76, 114)' }}>
              {docData.fileName} – {docData.title}
            </h2>
          </div>
          <button
            onClick={() => setShowDocPreview(false)}
            className="transition-all rounded-full p-1.5"
            style={{ color: 'rgba(18, 76, 114, 0.6)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgb(18, 76, 114)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'rgba(18, 76, 114, 0.6)';
            }}
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-hidden">
          {docData.fileBase64 ? (
            <iframe
              src={`data:${docData.mimeType};base64,${docData.fileBase64}`}
              title={`${docData.title} Document Preview`}
              className="w-full h-full border rounded-lg"
              style={{ borderColor: 'rgba(18, 76, 114, 0.1)' }}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }}>
                <svg className="w-8 h-8" style={{ color: 'rgba(18, 76, 114, 0.6)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-gray-600 font-medium">No document available for preview</p>
              <p className="text-sm mt-1" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>
                {docData.title} document could not be loaded
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateHiringRequestModal;